import { LOG_LEVEL } from "@vixi/types";

const logColors = {
  [LOG_LEVEL.DEBUG]: "#aaa",
  [LOG_LEVEL.INFO]: "#8af",
  [LOG_LEVEL.WARN]: "#f90",
  [LOG_LEVEL.ERROR]: "#f00",
};

class Logger {
  subscribers: ((logLevel: LOG_LEVEL, args: any[]) => void)[] = [];
  level: LOG_LEVEL = LOG_LEVEL.DEBUG;

  constructor() {
    this.subscribe = this.subscribe.bind(this);
    this.unsubscribe = this.unsubscribe.bind(this);
    this.debug = this.debug.bind(this);
    this.info = this.info.bind(this);
    this.warn = this.warn.bind(this);
    this.error = this.error.bind(this);
  }

  private next(logLevel: LOG_LEVEL, args: any[]) {
    this.subscribers.forEach((subscriber) => {
      subscriber(logLevel, args);
    });
  }

  subscribe(fn: (logLevel: LOG_LEVEL, args: any[]) => void) {
    this.subscribers.push(fn);
  }

  unsubscribe(fn: (arg: any) => void) {
    this.subscribers = this.subscribers.filter((subscriber) => subscriber !== fn);
  }

  debug(...args: any[]) {
    if (this.level > LOG_LEVEL.DEBUG) return;

    this.next(LOG_LEVEL.DEBUG, args);
  }

  info(...args: any[]) {
    if (this.level > LOG_LEVEL.INFO) return;

    this.next(LOG_LEVEL.INFO, args);
  }
  log(...args: any[]) {
    this.info(...args);
  }

  warn(...args: any[]) {
    if (this.level > LOG_LEVEL.WARN) return;

    this.next(LOG_LEVEL.WARN, args);
  }

  error(...args: any[]) {
    if (this.level > LOG_LEVEL.ERROR) return;

    this.next(LOG_LEVEL.ERROR, args);
  }

  static getLogLevel(importMetaEnv: ImportMetaEnv) {
    const envLogLevel = importMetaEnv.VITE_LOGGER_LEVEL as string;

    if (envLogLevel) {
      const logLevel = LOG_LEVEL[envLogLevel.toUpperCase() as keyof typeof LOG_LEVEL];
      if (logLevel !== undefined) return logLevel;
    }

    if (import.meta.env.MODE === "production") {
      return LOG_LEVEL.ERROR;
    }

    return LOG_LEVEL.DEBUG;
  }
}

const logger = new Logger();
const initDefaultLogger = (importMetaEnv: ImportMetaEnv) => {
  logger.level = Logger.getLogLevel(importMetaEnv);

  logger.subscribe((logLevel, args) => {
    const color = logColors[logLevel];
    const logLevelString = LOG_LEVEL[logLevel].toUpperCase();

    const logArgs = [
      `%c[${logLevelString}]`,
      `font-weight: bold; font-size: 14px; color:${color}`,
      ...args,
    ];

    switch (logLevel) {
      case LOG_LEVEL.DEBUG:
      case LOG_LEVEL.INFO:
        console.debug(...logArgs);
        break;
      case LOG_LEVEL.WARN:
        console.warn(...logArgs);
        break;
      case LOG_LEVEL.ERROR:
        console.error(...logArgs);
        break;
    }
  });
};

export default logger;
export { Logger, logger, initDefaultLogger };
